import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { t } from '@vitesse/base-ui/i18n';
import { LocaleController } from '@vitesse/base-ui/i18n';
import '@vitesse/base-ui/elements/vitesse-dialog';
import '@vitesse/base-ui/elements/inputs/vitesse-number-input';
import '@vitesse/base-ui/elements/inputs/vitesse-toggle';
import '@vitesse/base-ui/elements/inputs/vitesse-text-input';
import '@vitesse/base-ui/elements/inputs/vitesse-select';
import './vitesse-json-editor';
import { jsonError, isPretty, pretty } from './json-edit';
import type { SymbolOpResult } from '../../core/bridge/connect-client';

/** The five input shapes the editor renders. */
type Category = 'boolean' | 'integer' | 'number' | 'string' | 'object';

/** What the grid hands the editor when opening it for a row. `writeFn` performs
 *  the actual write (one symbol) and resolves the plugin's per-symbol result. */
export interface ValueEditTarget {
  symbolId: string; // display id (decoded path)
  dataType: string; // raw wire/grid type, e.g. "BOOL", "REAL", "DINT", "STRING"
  currentValue: unknown;
  /** Plugin-defined allowed values — when present the editor renders a dropdown
   *  of exactly these instead of a free input (e.g. a stacklight string-enum). */
  enumValues?: string[];
  writeFn: (value: boolean | string) => Promise<SymbolOpResult>;
}

/**
 * Normalize any vendor/wire data-type string to one of the five input shapes.
 *
 * EXPORTED because it is the ONLY thing standing between a number and a string on the wire. The
 * write encoder (connect-client `symbolValueInit`) switches on the logical name — `number`,
 * `integer`, `boolean` — while the catalog carries the vendor one — `REAL`, `DINT`, `BOOL`. This
 * function is the bridge, and a second caller that did not go through it sent every value as a
 * string: the assistant's writes were refused with "kind mismatch" on every numeric symbol, and
 * nothing in that path could say why. One mapper, every writer.
 */
export function categorize(dataType: string): Category {
  const t = (dataType || '').toUpperCase();
  if (t === 'BOOL' || t === 'BOOLEAN') return 'boolean';
  if (/^(U?(S|D|L)?INT|WORD|DWORD|LWORD|BYTE|INTEGER)$/.test(t)) return 'integer';
  if (t === 'REAL' || t === 'LREAL' || t === 'FLOAT' || t === 'DOUBLE' || t === 'NUMBER') return 'number';
  if (t === 'STRUCT' || t === 'ARRAY' || t === 'OBJECT') return 'object';
  // A broker topic's whole message. The integrations service types it "JSON"
  // deliberately outside the scalar vocabulary so both readers print it verbatim
  // (topic/flatten.go, jsonDataType) — which meant it fell through to 'string' here
  // and got a single-line text input for what is often a multi-line document.
  // 'object' is already exactly the right editor: a mono multi-line box that
  // JSON.parses before saving, so a malformed edit is refused in the dialog rather
  // than published to the customer's broker.
  if (t === 'JSON') return 'object';
  return 'string'; // STRING/WSTRING/CHAR/TIME/unknown → free text
}

const CATEGORY_I18N = {
  boolean: 'dataType.boolean',
  integer: 'dataType.integer',
  number: 'dataType.number',
  string: 'dataType.string',
  object: 'dataType.object',
} as const;

/**
 * <vitesse-value-editor> — modal editor for one symbol's value. Renders the
 * right input for the data type (bool→toggle, int/real→spinner, string→text,
 * object/array→JSON textarea), validates, writes via the supplied `writeFn`, and
 * surfaces the plugin's error (e.g. LOCKED) inline. Fully themed + i18n.
 *
 * Driven imperatively (the vanilla grid owns it): `editor.openFor(target)`.
 */
@customElement('vitesse-value-editor')
export class VitesseValueEditor extends LitElement {
  @state() private open = false;
  @state() private error: string | null = null;
  @state() private saving = false;
  @state() private value: boolean | string = '';
  private target?: ValueEditTarget;

  /** The JSON editor, when the value is an object — Format is driven from the
   *  header above it, so the button needs a handle on the element. */
  private get jsonEditor(): import('./vitesse-json-editor').VitesseJsonEditor | null {
    return this.renderRoot.querySelector('vitesse-json-editor');
  }

  constructor() {
    super();
    new LocaleController(this); // re-render all t() calls on language change
  }

  static styles = css`
    .info {
      display: flex;
      flex-direction: column;
      gap: 6px;
      margin-bottom: 14px;
    }
    .row {
      display: flex;
      gap: 8px;
      font-size: 13px;
    }
    .row .k {
      color: var(--text-secondary);
      min-width: 64px;
    }
    .row .v {
      color: var(--text-primary);
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      word-break: break-all;
    }
    .field-label {
      display: block;
      font-size: 12px;
      color: var(--text-secondary);
      margin-bottom: 6px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .json-head {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 6px;
    }
    .format {
      font: inherit;
      font-size: 12px;
      padding: 3px 9px;
      border-radius: 5px;
      border: 1px solid var(--border-color);
      background: var(--bg-primary);
      color: var(--text-secondary);
      cursor: pointer;
    }
    .format:hover:not(:disabled) {
      color: var(--blue);
      border-color: var(--blue);
    }
    .format:disabled {
      opacity: 0.45;
      cursor: default;
    }
    .json-where {
      font-size: 12px;
      color: var(--red);
    }
    .error {
      margin-top: 10px;
      padding: 8px 10px;
      border-radius: 6px;
      background: color-mix(in srgb, var(--red) 14%, transparent);
      border: 1px solid color-mix(in srgb, var(--red) 45%, transparent);
      color: var(--red);
      font-size: 13px;
      line-height: 1.45;
    }
  `;

  /** Open the editor for a symbol. Resets state from the target's current value. */
  openFor(target: ValueEditTarget): void {
    this.target = target;
    const cat = categorize(target.dataType);
    this.error = null;
    this.saving = false;
    if (cat === 'boolean') {
      this.value = target.currentValue === true || target.currentValue === 'true';
    } else if (cat === 'object') {
      // Indent on OPEN. A struct arrives as an object and was already formatted,
      // but a broker message arrives as a STRING and used to be shown exactly as
      // it came off the wire — one unreadable line. `pretty` returns its input
      // unchanged when it does not parse, so a value we cannot read is still
      // shown verbatim and still editable rather than blanked.
      //
      // NO VALUE YET → an EMPTY box, never the text "undefined" or "null". The
      // grid opens the editor for a row that has not reported (a quiet topic on a
      // broker with no retained message), and JSON.stringify(undefined) yields
      // undefined, which String() would then render as nine bytes the user never
      // typed — and validate() would accept as a save. Blank is refused instead.
      if (target.currentValue == null) {
        this.value = '';
      } else {
        try {
          this.value = typeof target.currentValue === 'string' ? pretty(target.currentValue) : JSON.stringify(target.currentValue, null, 2);
        } catch {
          this.value = String(target.currentValue ?? '');
        }
      }
    } else {
      this.value = target.currentValue == null ? '' : String(target.currentValue);
    }
    this.open = true;
  }

  private close() {
    this.open = false;
    this.target = undefined;
  }

  private validate(cat: Category): string | null {
    if (cat === 'integer' || cat === 'number') {
      if (String(this.value).trim() === '' || isNaN(parseFloat(String(this.value)))) {
        return t('valueEditor.invalidNumber');
      }
    }
    if (cat === 'object') {
      // An empty box is not valid JSON, and `jsonError` deliberately stays quiet
      // about it while someone is retyping — so the emptiness check is here,
      // where saving is decided, rather than in the parser.
      if (String(this.value).trim() === '') return t('valueEditor.invalidJson');
      const err = jsonError(String(this.value));
      // Name the line and column WHEN WE KNOW THEM. "Invalid JSON" against a
      // 40-line message says only that something is wrong somewhere, which is
      // the least useful true thing a refusal can say — but a made-up line is
      // worse still, so an unlocatable error falls back to the plain wording.
      if (err && err.line !== undefined) {
        return t('valueEditor.invalidJsonAt', { line: err.line, column: err.column, message: err.message });
      }
      if (err) return t('valueEditor.invalidJson');
    }
    return null;
  }

  private async save() {
    if (!this.target) return;
    const cat = categorize(this.target.dataType);
    const invalid = this.validate(cat);
    if (invalid) {
      this.error = invalid;
      return;
    }
    this.saving = true;
    this.error = null;
    try {
      const res = await this.target.writeFn(this.value);
      if (res.ok) {
        this.close();
      } else {
        // Surface the plugin's structured error verbatim (e.g. the LOCKED message).
        this.error = res.error || t('valueEditor.writeFailed');
      }
    } catch (e) {
      this.error = e instanceof Error ? e.message : t('valueEditor.writeFailed');
    } finally {
      this.saving = false;
    }
  }

  private renderInput(cat: Category) {
    const onChange = (e: CustomEvent) => {
      this.value = cat === 'boolean' ? e.detail.checked : e.detail.value;
      if (this.error) this.error = null; // clear stale error on edit
    };
    // Enter in a SINGLE-LINE box saves, as it would in any form. Attached to the
    // string and number inputs only: in the JSON editor Enter inserts a newline,
    // and the toggle and enum select have their own keyboard behaviour. The
    // input's value is already in this.value by the time the key lands (the
    // components emit `change` on every keystroke), so save() reads the same text
    // the user sees. Native keydown is composed, so it crosses the component's
    // shadow root to reach this listener on the host element. A key pressed
    // mid-IME-composition is the composition's, not ours.
    const onEnter = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.isComposing || this.saving) return;
      e.preventDefault();
      void this.save();
    };
    // Enum override (plugin-defined value set) → a dropdown of exactly those,
    // regardless of the underlying data type (e.g. a STRING stacklight enum).
    const enums = this.target?.enumValues;
    if (enums && enums.length) {
      return html`<vitesse-select
        .options=${enums.map((v) => ({ value: v, label: v }))}
        .value=${String(this.value)}
        @change=${onChange}
      ></vitesse-select>`;
    }
    switch (cat) {
      case 'boolean':
        return html`<vitesse-toggle size="lg" ?checked=${this.value === true} @change=${onChange}></vitesse-toggle>`;
      case 'integer':
        return html`<vitesse-number-input
          size="md"
          integer
          step="1"
          .value=${String(this.value)}
          @change=${onChange}
          @keydown=${onEnter}
        ></vitesse-number-input>`;
      case 'number':
        return html`<vitesse-number-input
          size="md"
          step="0.1"
          .value=${String(this.value)}
          @change=${onChange}
          @keydown=${onEnter}
        ></vitesse-number-input>`;
      case 'object': {
        const text = String(this.value);
        const err = jsonError(text);
        // Offered only when it would DO something: disabled while the text does
        // not parse (there is nothing to reformat) and while it is already
        // formatted, so the control never sits there as a no-op.
        const canFormat = !err && text.trim() !== '' && !isPretty(text);
        return html`
          <div class="json-head">
            <button
              class="format"
              type="button"
              ?disabled=${!canFormat}
              title=${t('valueEditor.formatHint')}
              @click=${() => this.jsonEditor?.format()}
            >
              ${t('valueEditor.format')}
            </button>
            ${
              err && err.line !== undefined
                ? html`<span class="json-where">${t('valueEditor.jsonAt', { line: err.line, column: err.column })}</span>`
                : ''
            }
          </div>
          <vitesse-json-editor rows="12" .value=${text} @change=${onChange}></vitesse-json-editor>
        `;
      }
      default:
        return html`<vitesse-text-input
          size="md"
          .value=${String(this.value)}
          @change=${onChange}
          @keydown=${onEnter}
        ></vitesse-text-input>`;
    }
  }

  render() {
    if (!this.target) return html`<vitesse-dialog .open=${false}></vitesse-dialog>`;
    const cat = categorize(this.target.dataType);
    return html`
      <vitesse-dialog
        .open=${this.open}
        heading=${t('valueEditor.title')}
        closeLabel=${t('common.close')}
        @dialog-close=${() => this.close()}
      >
        <div class="info">
          <div class="row"><span class="k">${t('valueEditor.symbol')}</span><span class="v">${this.target.symbolId}</span></div>
          <div class="row"><span class="k">${t('valueEditor.type')}</span><span class="v">${t(CATEGORY_I18N[cat])}</span></div>
        </div>
        <span class="field-label">${t('valueEditor.value')}</span>
        ${this.renderInput(cat)} ${this.error ? html`<div class="error">${this.error}</div>` : ''}
        <vitesse-button slot="footer" color="blue" filled ?disabled=${this.saving} @click=${() => this.save()}>
          ${this.saving ? t('valueEditor.saving') : t('valueEditor.save')}
        </vitesse-button>
      </vitesse-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-value-editor': VitesseValueEditor;
  }
}
